import { Modal } from 'framework/ui/ui-builder';
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { CustomerReport, APPROVE_CUSTOMER_REPORT } from 'domain/shared/newProject';
import { useAuthorization } from 'framework/authorization';

interface ExistingReportModalProps {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  inApprovalReport?: CustomerReport;
  rejectedReport?: CustomerReport;
}

const Highlight: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
    {children}
  </Box>
);

const ExistingReportModal = ({
  modalOpen,
  setModalOpen,
  inApprovalReport,
  rejectedReport,
}: ExistingReportModalProps) => {
  const { isGranted } = useAuthorization();
  const canApprove = isGranted(APPROVE_CUSTOMER_REPORT);

  const message = useMemo(() => {
    if (rejectedReport) {
      return (
        <>
          Please set the status of the existing <Highlight>Rejected</Highlight> report to{' '}
          <Highlight>Canceled</Highlight> or <Highlight>In approval</Highlight>.
        </>
      );
    }

    if (inApprovalReport) {
      return canApprove ? (
        <>
          Please approve or reject report with status <Highlight>In approval</Highlight>.
        </>
      ) : (
        <>
          Please wait until the report changes to the status <Highlight>Approved</Highlight>.
        </>
      );
    }

    return null;
  }, [rejectedReport, inApprovalReport, canApprove]);

  if (!rejectedReport && !inApprovalReport) {
    return null;
  }

  return (
    <Modal
      isOpen={modalOpen}
      onToggle={() => setModalOpen(false)}
      hasCloseIcon
      hasCancelButton={false}
      title="Not approved customer report already exists"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body1">The system contains a report that has not been approved.</Typography>
        <Typography variant="body1">{message}</Typography>
      </Box>
    </Modal>
  );
};

export default ExistingReportModal;
